import { take, takeLeading, cancel, cancelled, call, put, select } from 'redux-saga/effects';
import ky from 'ky-universal';
import { stringify } from 'query-string';

import { apiHost } from '../../config/api';
import baseFields from '../../config/api/baseFields';
import { AUTH_STATE_LOGIN } from '../../constants/state';
import { SUBSCRIPTION_TYPE_VIP, SUBSCRIPTION_TYPE_PACKAGE, PaymentProviderVip } from '../../constants/payment';
import { unsubscriptionSelector, vipSubscriptionSelector, packageSubscriptionEntitiesSelector } from '../selector/user';
import { isMobileSelector } from '../selector/device';
import { execCancelOrder } from '../utils/ga/category01';
import polling, { POLLING_PENDING, POLLING_SUCCESS, POLLING_FAILED } from '../utils/polling';
import authUtils from '../utils/auth';

import {
  AUTH_FLOW_END,
  LOGOUT,
  UNSUBSCRIBE,
  UNSUBSCRIBE_REQUESTING,
  UNSUBSCRIBE_FAILED,
  UNSUBSCRIBE_SUCCEEDED,
  FETCH_PRODUCT_SUBSCRIPTIONS,
} from '../constants/actionTypes';

const stringifiedBaseFields = stringify(baseFields);

/**
 * @typedef {object} VIPUnsubscribeInfo
 * @property {string} orderId
 * @property {string} productId
 * @property {string} [code]
 * @property {boolean} [isMobile]
 * @property {AbortSignal} [signal]
 */

/** @param {VIPUnsubscribeInfo} unsubscribeInfo */
async function unsubscribeSonet(unsubscribeInfo) {
  const { orderId, productId, signal } = unsubscribeInfo;
  await ky.post(`${apiHost}/v1/purchase/cancel/sonetTel`, {
    searchParams: stringifiedBaseFields,
    headers: {
      Authorization: authUtils.getAccessToken(),
    },
    json: {
      orderId,
      productId,
    },
    signal,
  });
}

/** @param {VIPUnsubscribeInfo} unsubscribeInfo */
async function unsubscribeLinePay(unsubscribeInfo) {
  const { orderId, productId, signal, isMobile } = unsubscribeInfo;
  await ky.post(`${apiHost}/v1/purchase/cancel/linepay`, {
    searchParams: stringifiedBaseFields,
    headers: {
      Authorization: authUtils.getAccessToken(),
    },
    json: {
      orderId,
      productId,
      platform: isMobile ? 'mobile_web' : 'desktop_web',
    },
    signal,
  });
}

/** @param {VIPUnsubscribeInfo} unsubscribeInfo */
async function unsubscribeLineMobile(unsubscribeInfo) {
  const { orderId, productId, signal } = unsubscribeInfo;
  const accessToken = authUtils.getAccessToken();

  await ky.post(`${apiHost}/v1/purchase/linemobile/unsubscribe`, {
    json: {
      ...baseFields,
      orderId,
      productId,
    },
    headers: {
      Authorization: accessToken,
    },
    signal,
  });

  async function checkUnsubscribeStatus() {
    const res = await ky.get(`${apiHost}/v1/purchase/linemobile/unsubscribe/${orderId}`, {
      searchParams: stringifiedBaseFields,
      headers: {
        Authorization: accessToken,
      },
      signal,
    });
    return await res.json();
  }

  function shouldCheckUnsubscribeStatus(unsubscribeStatus) {
    switch (unsubscribeStatus.code) {
      case 2008:
        return POLLING_PENDING;
      case 2009:
        return POLLING_SUCCESS;
      default:
        return POLLING_FAILED;
    }
  }

  await polling(checkUnsubscribeStatus, shouldCheckUnsubscribeStatus, { signal });
}

/** @param {VIPUnsubscribeInfo} unsubscribeInfo */
async function unsubscribeTapPay(unsubscribeInfo) {
  const { orderId, productId, signal } = unsubscribeInfo;
  await ky.post(`${apiHost}/v1/purchase/cancel/tappay`, {
    searchParams: stringifiedBaseFields,
    headers: {
      Authorization: authUtils.getAccessToken(),
    },
    json: {
      orderId,
      productId,
    },
    signal,
  });
}

/** @param {VIPUnsubscribeInfo} unsubscribeInfo */
async function unsubscribePromotionCode(unsubscribeInfo) {
  const { orderId, productId, code, signal } = unsubscribeInfo;
  await ky.post(`${apiHost}/v1/purchase/cancel/promotionCode`, {
    searchParams: stringifiedBaseFields,
    headers: {
      Authorization: authUtils.getAccessToken(),
    },
    json: {
      orderId,
      productId,
      promotionCode: code,
    },
    signal,
  });
}

/**
 * @param {object} unsubscribeInfo
 * @param {number} unsubscribeInfo.dealId
 * @param {AbortSignal} unsubscribeInfo.signal
 */
async function unsubscribePackage(unsubscribeInfo) {
  const { dealId, signal } = unsubscribeInfo;
  await ky.post(`${apiHost}/purchase/v2/cancel`, {
    searchParams: stringifiedBaseFields,
    headers: {
      Authorization: authUtils.getAccessToken(),
    },
    json: {
      dealId,
    },
    signal,
  });
}

function* unsubscribe() {
  execCancelOrder();

  const abortController = new AbortController();
  const signal = abortController.signal;

  try {
    yield put({ type: UNSUBSCRIBE_REQUESTING });

    const unsubscription = yield select(unsubscriptionSelector);
    const type = unsubscription?.type;

    switch (unsubscription?.type) {
      case SUBSCRIPTION_TYPE_VIP: {
        const { source, orderId, productId, code } = yield select(vipSubscriptionSelector);
        const isMobile = yield select(isMobileSelector);

        const unsubscribeFn = {
          [PaymentProviderVip.SonetTel]: unsubscribeSonet,
          [PaymentProviderVip.LinePay]: unsubscribeLinePay,
          [PaymentProviderVip.LineMobile]: unsubscribeLineMobile,
          [PaymentProviderVip.TapPay]: unsubscribeTapPay,
          [PaymentProviderVip.PromotionCode]: unsubscribePromotionCode,
        }[source];

        if (typeof unsubscribeFn !== 'function') throw new Error('No such payment provider');

        yield call(unsubscribeFn, {
          orderId,
          productId: String(productId),
          code,
          isMobile,
          signal,
        });

        yield put({
          type: UNSUBSCRIBE_SUCCEEDED,
          payload: { type, timestamp: Date.now() }
        });
        yield put({ type: FETCH_PRODUCT_SUBSCRIPTIONS });
        break;
      }

      case SUBSCRIPTION_TYPE_PACKAGE: {
        const packageSubscriptionEntities = yield select(packageSubscriptionEntitiesSelector);
        const dealId = packageSubscriptionEntities[unsubscription.id]?.srcInfo.deal;
        yield call(unsubscribePackage, { dealId, signal });
        yield put({
          type: UNSUBSCRIBE_SUCCEEDED,
          payload: { type, dealId, timestamp: Date.now() },
        });
        break;
      }

      default: {
        throw new Error('No such subscription type');
      }
    }
  } catch (error) {
    yield put({ type: UNSUBSCRIBE_FAILED, payload: error, error: true });
  } finally {
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}

export function* unsubscribeFlow() {
  while (true) {
    const { payload } = yield take(AUTH_FLOW_END);

    if (payload === AUTH_STATE_LOGIN) {
      const task = yield takeLeading(UNSUBSCRIBE, unsubscribe);
      yield take(LOGOUT);
      yield cancel(task);
    }
  }
}
